import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import Container from 'react-bootstrap/Container'
import { Row, Col, Card, Table, Button } from 'react-bootstrap'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import api from 'api/api'
import { toast } from 'react-toastify'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { LoadingSpinner } from 'components/LoadingSpinner'
import TopHeader from 'components/TopHeader'
import { getTipoColor, getTipoTexto, getEstadoColor, getEstadoTexto } from 'config/pedidos'
import { getDisplayDateTime } from 'utils/date'

const Dashboard = () => {
	const history = useHistory()
	const [loading, setLoading] = useState(true)
	const [dashboardData, setDashboardData] = useState(null)
	const [historicoPedidos, setHistoricoPedidos] = useState([])
	const [historicoVentas, setHistoricoVentas] = useState([])
	const [ultimosPedidos, setUltimosPedidos] = useState([])

	useEffect(() => {
		getDashboardData()
	}, [])

	const getDashboardData = async () => {
		try {
			setLoading(true)
			const response = await api.get('a/dashboard')

			if (response.error) {
				toast.error(response.msg)
				return
			}

			const datos = response.datos
			setDashboardData(datos)

			// Procesar datos para gráficos
			procesarHistoricoPedidos(datos.historicoPedidos)
			procesarHistoricoVentas(datos.historicoVentas)
			procesarUltimosPedidos(datos.ultimosPedidos)

		} catch (error) {
			console.error('Error al obtener datos del dashboard:', error)
			toast.error('Error al cargar el dashboard')
		} finally {
			setLoading(false)
		}
	}

	const procesarHistoricoPedidos = (data) => {
		if (!data || !Array.isArray(data)) return

		const meses = [
			'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
			'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
		]

		const chartData = data.map(item => ({
			mes: meses[item.mes - 1],
			pedidos: item.cantidad,
			mesNumero: item.mes,
			anho: item.anho
		}))

		setHistoricoPedidos(chartData)
	}

	const procesarHistoricoVentas = (data) => {
		if (!data || !Array.isArray(data)) return

		const meses = [
			'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
			'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
		]

		const chartData = data.map(item => ({
			mes: meses[item.mes - 1],
			ventas: item.monto,
			mesNumero: item.mes,
			anho: item.anho
		}))

		setHistoricoVentas(chartData)
	}

	const procesarUltimosPedidos = (data) => {
		if (!data || !Array.isArray(data)) return

		const pedidosProcesados = data.map(pedido => {
			return {
				...pedido,
				estadoColor: getEstadoColor(pedido.estado),
				estadoTexto: getEstadoTexto(pedido.estado),
				tipoColor: getTipoColor(pedido.tipo),
				tipoTexto: getTipoTexto(pedido.tipo)
			}
		})

		setUltimosPedidos(pedidosProcesados)
	}

	const formatearMonto = (monto) => {
		return formatNumberForDisplay(monto.toString()) + ' Gs.'
	}

	if (loading) {
		return (
			<Container fluid id="home" className="vh-100">
				<TopHeader
					iconName="home"
					text="Inicio"
				/>
				<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
					<LoadingSpinner />
				</div>
			</Container>
		)
	}

	if (!dashboardData) {
		return (
			<Container fluid id="home" className="vh-100">
				<TopHeader
					iconName="home"
					text="Inicio"
				/>
				<div className="alert alert-warning">
					No se pudieron cargar los datos del dashboard
				</div>
			</Container>
		)
	}

	// Las métricas ya vienen calculadas del servidor
	const metricas = dashboardData.metricasPrincipales

	return (
		<Container fluid id="home" className="vh-100">
			<TopHeader
				iconName="home"
				text="Inicio"
			/>

			{/* Métricas principales */}
			<Row className="mb-4">
				<Col lg={3} md={6} className="mb-3">
					<Card className="h-100">
						<Card.Body className="text-center">
							<div className="mb-2">
								<i className="fas fa-shopping-cart fa-2x text-primary"></i>
							</div>
							<h5 className="card-title">Pedidos en curso</h5>
							<h3 className="text-primary">{metricas.pedidosEnCurso}</h3>
						</Card.Body>
					</Card>
				</Col>

				<Col lg={3} md={6} className="mb-3">
					<Card className="h-100">
						<Card.Body className="text-center">
							<div className="mb-2">
								<i className="fas fa-calendar-day fa-2x text-warning"></i>
							</div>
							<h5 className="card-title">Pedidos del día</h5>
							<h3 className="text-warning">{metricas.pedidosDelDia}</h3>
						</Card.Body>
					</Card>
				</Col>

				<Col lg={3} md={6} className="mb-3">
					<Card className="h-100">
						<Card.Body className="text-center">
							<div className="mb-2">
								<i className="fas fa-dollar-sign fa-2x text-success"></i>
							</div>
							<h5 className="card-title">Ventas del día</h5>
							<h3 className="text-success">{formatearMonto(metricas.ventasDelDia)}</h3>
						</Card.Body>
					</Card>
				</Col>

				<Col lg={3} md={6} className="mb-3">
					<Card className="h-100">
						<Card.Body className="text-center">
							<div className="mb-2">
								<i className="fas fa-receipt fa-2x text-info"></i>
							</div>
							<h5 className="card-title">Ticket promedio</h5>
							<h3 className="text-info">{formatearMonto(metricas.ticketPromedioDia)}</h3>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Gráficos */}
			<Row className="mb-4">
				<Col lg={6} className="mb-3">
					<Card className="h-100">
						<Card.Header>
							<h5 className="mb-0">
								<i className="fas fa-chart-bar me-2"></i>
								Histórico Pedidos (últimos 12 meses)
							</h5>
						</Card.Header>
						<Card.Body>
							<ResponsiveContainer width="100%" height={350}>
								<BarChart
									data={historicoPedidos}
									margin={{ top: 20, right: 30, bottom: 60, left: 20 }}
								>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis
										dataKey="mes"
										type="category"
										interval={0}
										tick={{ fontSize: 14 }}
										angle={-45}
										textAnchor="end"
										height={70}
									/>
									<YAxis
										type="number"
										domain={[0, 'dataMax']}
										tickFormatter={(value) => Math.round(value)}
										allowDecimals={false}
									/>
									<Tooltip />
									<Legend />
									<Bar
										dataKey="pedidos"
										fill="#007bff"
										name="Pedidos"
									/>
								</BarChart>
							</ResponsiveContainer>
						</Card.Body>
					</Card>
				</Col>

				<Col lg={6} className="mb-3">
					<Card className="h-100">
						<Card.Header>
							<h5 className="mb-0">
								<i className="fas fa-chart-line me-2"></i>
								Histórico Ventas (últimos 12 meses)
							</h5>
						</Card.Header>
						<Card.Body>
							<ResponsiveContainer width="100%" height={350}>
								<BarChart
									data={historicoVentas}
									margin={{ top: 20, right: 30, bottom: 60, left: 20 }}
								>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis
										dataKey="mes"
										type="category"
										interval={0}
										tick={{ fontSize: 14 }}
										angle={-45}
										textAnchor="end"
										height={70}
									/>
									<YAxis
										type="number"
										domain={[0, 'dataMax']}
										tickFormatter={(value) => formatNumberForDisplay(value.toString())}
									/>
									<Tooltip formatter={(value) => formatearMonto(value)} />
									<Legend />
									<Bar
										dataKey="ventas"
										fill="#28a745"
										name="Ventas (Gs.)"
									/>
								</BarChart>
							</ResponsiveContainer>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Últimos pedidos */}
			<Row>
				<Col>
					<Card>
						<Card.Header className="d-flex justify-content-between align-items-center">
							<h5 className="mb-0">
								<i className="fas fa-list me-2"></i>
								Últimos pedidos
							</h5>
							<Button
								variant="outline-primary"
								size="sm"
								onClick={() => history.push('/pedidos')}
							>
								<i className="fas fa-eye me-1"></i>
								Ver todos
							</Button>
						</Card.Header>
						<Card.Body>
							{ultimosPedidos.length > 0 ? (
								<div className="table-responsive">
									<Table hover>
										<thead>
											<tr>
												<th>ID</th>
												<th>Cliente</th>
												<th>Fecha</th>
												<th>Tipo</th>
												<th>Monto total</th>
												<th>Estado</th>
											</tr>
										</thead>
										<tbody>
											{ultimosPedidos.map((pedido, index) => (
												<tr key={pedido.id || index}>
													<td className="fw-bold">{pedido.id}</td>
													<td>{pedido.cliente}</td>
													<td>{getDisplayDateTime(pedido.fecha)}</td>
													<td>
														<span className={`badge bg-${pedido.tipoColor}`}>
															{pedido.tipoTexto}
														</span>
													</td>
													<td className="fw-bold">{formatearMonto(pedido.montoTotal)}</td>
													<td>
														<span className={`badge bg-${pedido.estadoColor}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
															<i className="fas fa-circle me-1" style={{ fontSize: '4px' }}></i>
															{pedido.estadoTexto}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>
							) : (
								<div className="text-center text-muted py-5">
									<i className="fas fa-inbox fa-3x mb-3"></i>
									<p>No hay pedidos registrados</p>
								</div>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export default Dashboard